import { Button, toast } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { GitMerge, PlusIcon } from "lucide-react";
import { useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type {
  CatalogSkuCreateInput,
  CatalogSkuUpdateInput,
} from "@/modules/catalog/domain/contracts";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import type { CatalogSkuDTO } from "../api/client";
import {
  useCatalogProductQuery,
  useCatalogProductSkusQuery,
  useCatalogVehicleConfigurationsQuery,
  useCreateCatalogFitment,
  useCreateCatalogSku,
  useDeleteCatalogFitment,
  useDeleteCatalogSku,
  useUpdateCatalogSku,
} from "../api/queries";
import { CatalogSkusFilter } from "../ui/CatalogSkusFilter";
import { CatalogSkusTable } from "../ui/CatalogSkusTable";
import { CatalogMergeModal } from "../ui/CatalogMergeModal";
import { FitmentsModal } from "../ui/FitmentsModal";
import { SkuEPCModal } from "../ui/SkuEPCModal";
import { SkuFormModal } from "../ui/SkuFormModal";
import { SkuMediaModal } from "../ui/SkuMediaModal";

const DEFAULT_SKU_LIMIT = 20;

export function CatalogProductDetailPage() {
  const { id } = useParams({ from: "/app/catalog/$id" });
  const nav = useNavigate({ from: "/app/catalog/$id" });
  const [skuPage, setSkuPage] = useState({
    limit: DEFAULT_SKU_LIMIT,
    offset: 0,
  });
  const [skuFilter, setSkuFilter] = useState<{
    search: string;
    condition?: string;
  }>({ search: "" });

  const productId = id ?? "";
  const productQuery = useCatalogProductQuery(productId);
  const skusQuery = useCatalogProductSkusQuery(productId, {
    limit: skuPage.limit,
    offset: skuPage.offset,
    search: skuFilter.search?.trim() || undefined,
    condition: skuFilter.condition as "new" | "used" | "refurb" | undefined,
  });
  const vehicleConfigurationsQuery = useCatalogVehicleConfigurationsQuery();

  const createSku = useCreateCatalogSku(productId);
  const updateSku = useUpdateCatalogSku(productId);
  const deleteSku = useDeleteCatalogSku(productId);
  const [fitmentsSkuId, setFitmentsSkuId] = useState<string | null>(null);
  const [skuEpcId, setSkuEpcId] = useState<string | null>(null);
  const [skuMediaId, setSkuMediaId] = useState<string | null>(null);
  const createFitment = useCreateCatalogFitment(fitmentsSkuId ?? "");
  const deleteFitment = useDeleteCatalogFitment(fitmentsSkuId ?? "");

  const canUpdate = useActionPermission(["catalog:update"]);
  const [skuModalOpen, setSkuModalOpen] = useState(false);
  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  const [editingSku, setEditingSku] = useState<CatalogSkuDTO | null>(null);

  const product = productQuery.data;
  const skuListResult = skusQuery.data;
  const skus = skuListResult?.data ?? [];
  const skuMeta = skuListResult?.meta ?? {
    total: 0,
    limit: skuPage.limit,
    offset: skuPage.offset,
  };

  const handleSkuFilterChange = (updates: {
    search?: string;
    condition?: string;
  }) => {
    setSkuFilter((prev) => ({ ...prev, ...updates }));
    setSkuPage((prev) => ({ ...prev, offset: 0 }));
  };

  if (productQuery.isLoading || !product) {
    return (
      <>
        <Header>
          <Breadcrumb
            items={[
              { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
              { label: "Catalog", to: "/app/catalog" },
              { label: "..." },
            ]}
          />
        </Header>
        <Main>
          <p className="text-muted-foreground">ກຳລັງໂຫຼດ...</p>
        </Main>
      </>
    );
  }

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "Catalog", to: "/app/catalog" },
            { label: product.name },
          ]}
        />
      </Header>

      <Main className="space-y-6">
        <div className="mb-2 flex flex-wrap items-center justify-between space-y-2">
          <div>
            <h2 className="font-bold text-2xl tracking-tight">
              {product.name}
            </h2>
            <p className="text-muted-foreground text-sm">
              ລາຍລະອຽດສິນຄ້າ ແລະ ຈັດການ SKU ຫຼັກ
            </p>
          </div>
          {canUpdate && product.status !== "archived" ? (
            <Button
              type="button"
              variant="outline"
              className="gap-1"
              onClick={() => setMergeModalOpen(true)}
            >
              <GitMerge className="size-4" />
              ລວມເຂົ້າ master
            </Button>
          ) : null}
        </div>

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <div className="mb-2 flex flex-row items-center justify-between px-2">
            <h3 className="font-semibold text-base">SKU ຫຼັກ</h3>
            {canUpdate && (
              <Button
                onClick={() => {
                  setEditingSku(null);
                  setSkuModalOpen(true);
                }}
              >
                <PlusIcon className="size-4" />
                ເພີ່ມ SKU
              </Button>
            )}
          </div>
          <CatalogSkusFilter
            search={skuFilter.search}
            condition={skuFilter.condition}
            onFilterChange={handleSkuFilterChange}
          />
          <CatalogSkusTable
            skus={skus}
            canManage={!!canUpdate}
            isLoading={skusQuery.isLoading}
            offset={skuMeta.offset}
            limit={skuMeta.limit}
            totalCount={skuMeta.total}
            onPaginationChange={(offset, limit) =>
              setSkuPage((prev) => ({ ...prev, offset, limit }))
            }
            onEdit={(sku) => {
              setEditingSku(sku);
              setSkuModalOpen(true);
            }}
            onDelete={async (skuId) => {
              try {
                await deleteSku.mutateAsync(skuId);
                toast.success("ລຶບ SKU ສຳເລັດ");
                await skusQuery.refetch();
              } catch {
                toast.error("ມີຮ້ານໃຊ້ SKU ນີ້ຢູ່ ບໍ່ສາມາດລຶບໄດ້");
              }
            }}
            onManageFitments={(skuId) => setFitmentsSkuId(skuId)}
            onManageSkuEpc={(skuId) => setSkuEpcId(skuId)}
            onManageMedia={(skuId) => setSkuMediaId(skuId)}
          />
        </div>
      </Main>

      <SkuFormModal
        open={skuModalOpen}
        onOpenChange={(open) => {
          setSkuModalOpen(open);
          if (!open) setEditingSku(null);
        }}
        editingSku={editingSku}
        onSubmit={async (
          vals: Omit<CatalogSkuCreateInput, "catalogProductId">,
        ) => {
          try {
            if (editingSku) {
              await updateSku.mutateAsync({
                id: editingSku.id,
                input: vals as CatalogSkuUpdateInput,
              });
              toast.success("ແກ້ໄຂ SKU ສຳເລັດ");
            } else {
              await createSku.mutateAsync(vals);
              toast.success("ເພີ່ມ SKU ສຳເລັດ");
            }
            setSkuModalOpen(false);
            setEditingSku(null);
            await skusQuery.refetch();
          } catch {
            toast.error(editingSku ? "ແກ້ໄຂລົ້ມເຫຼວ" : "ເພີ່ມລົ້ມເຫຼວ");
          }
        }}
      />

      {fitmentsSkuId && (
        <FitmentsModal
          skuId={fitmentsSkuId}
          open={!!fitmentsSkuId}
          onOpenChange={(open) => !open && setFitmentsSkuId(null)}
          vehicleConfigurations={vehicleConfigurationsQuery.data?.data ?? []}
          onCreateFitment={async (vehicleConfigurationId, opts) => {
            await createFitment.mutateAsync({
              vehicleConfigurationId,
              ...opts,
            });
          }}
          onDeleteFitment={async (fitmentId) => {
            await deleteFitment.mutateAsync(fitmentId);
          }}
        />
      )}

      {skuEpcId && (
        <SkuEPCModal
          skuId={skuEpcId}
          open={!!skuEpcId}
          onOpenChange={(open) => !open && setSkuEpcId(null)}
        />
      )}

      {skuMediaId && (
        <SkuMediaModal
          skuId={skuMediaId}
          open={!!skuMediaId}
          onOpenChange={(open) => !open && setSkuMediaId(null)}
          canManage={!!canUpdate}
        />
      )}

      <CatalogMergeModal
        open={mergeModalOpen}
        onOpenChange={setMergeModalOpen}
        sourceProductId={productId}
        sourceProductName={product.name}
        onMerged={(targetProductId) => {
          void nav({ to: "/app/catalog/$id", params: { id: targetProductId } });
        }}
      />
    </>
  );
}
